import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS, REQUEST_STATUSES, type RequestStatus } from '@navicosa/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Input, Select, Space, Switch, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import {
  phoneLocal,
  REQUEST_STATUS_COLOR,
  REQUEST_STATUS_LABEL,
  type RequestListItem,
  SOURCE_LABEL,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

/** Danh sách yêu cầu thuê (PRD 6.5). Sale chỉ thấy yêu cầu được phân công – máy chủ tự lọc. */
export function RequestListPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const canAssign = can(PERMISSIONS.REQUESTS_ASSIGN);
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get('q') ?? '');
  const page = Number(sp.get('page') ?? 1);
  const status = (sp.get('status') as RequestStatus | null) ?? undefined;
  const unassigned = sp.get('unassigned') === 'true';
  const set = (k: string, v?: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setSp(next);
  };
  const list = useQuery({
    queryKey: ['requests', page, status, unassigned, sp.get('q')],
    queryFn: () =>
      api.page<RequestListItem>('/admin/rental-requests', {
        page,
        limit: 20,
        status,
        unassigned: unassigned || undefined,
        q: sp.get('q'),
      }),
    refetchInterval: 60_000,
  });

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Yêu cầu thuê
        </Typography.Title>
      }
      extra={
        can(PERMISSIONS.REQUESTS_MANAGE) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => nav('/requests/new')}>
            Nhập yêu cầu (điện thoại/Zalo)
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Mã YC, tên, công ty, SĐT"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={(v) => set('q', v.trim() || undefined)}
          style={{ width: 280 }}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 200 }}
          value={status}
          onChange={(v) => set('status', v)}
          options={REQUEST_STATUSES.map((s) => ({ value: s, label: REQUEST_STATUS_LABEL[s] }))}
        />
        {canAssign && (
          <Space>
            <Switch
              checked={unassigned}
              onChange={(v) => set('unassigned', v ? 'true' : undefined)}
            />
            Chưa phân công
          </Space>
        )}
      </Space>
      <Table<RequestListItem>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: (p) => set('page', String(p)),
          showTotal: (t) => `${t} yêu cầu`,
        }}
        locale={{
          emptyText: canAssign ? 'Chưa có yêu cầu' : 'Chưa có yêu cầu nào được phân công cho bạn',
        }}
        onRow={(r) => ({ onClick: () => nav(`/requests/${r.id}`), style: { cursor: 'pointer' } })}
        columns={[
          {
            title: 'Mã',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Link to={`/requests/${r.id}`} onClick={(e) => e.stopPropagation()}>
                  {r.request_code}
                </Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {dayjs(r.created_at).format('DD/MM HH:mm')}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: 'Khách',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <span>
                  {r.contact_name}
                  {r.contact_company ? ` · ${r.contact_company}` : ''}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {phoneLocal(r.contact_phone)}
                </Typography.Text>
              </Space>
            ),
          },
          { title: 'Thiết bị', dataIndex: 'items_summary', ellipsis: true },
          { title: 'Khu vực', render: (_, r) => r.location?.name ?? '—' },
          { title: 'Bắt đầu', render: (_, r) => dayjs(r.rental_start_at).format('DD/MM/YYYY') },
          { title: 'Nguồn', render: (_, r) => SOURCE_LABEL[r.source] ?? r.source },
          {
            title: 'Phụ trách',
            render: (_, r) => r.assigned_user?.full_name ?? <Tag color="red">Chưa phân công</Tag>,
          },
          {
            title: 'Trạng thái',
            render: (_, r) => (
              <Tag color={REQUEST_STATUS_COLOR[r.status]}>{REQUEST_STATUS_LABEL[r.status]}</Tag>
            ),
          },
        ]}
      />
    </Card>
  );
}
